import React, { useEffect } from 'react';
import type { AuthUser } from '../../types';
import { useNavigate } from 'react-router-dom';
import DashboardHome from '../../features/dashboard/DashboardHome';
import { MODULES, MODULE_CATEGORIES, SETTINGS_TILE } from '../../utils/constants';
import { hasModuleAccess, isAdmin as checkAdmin, isTeacherLogin } from '../../utils/auth';
import { useChurchConfig } from '../../hooks';
import { parseHiddenModules, parseModuleTitles, parseCategoryOrder, parseModuleOrder, arrangeModules } from '../../utils/churchSettings';
import { pathForModuleId } from '../../routes';

const LAST_VISITED_KEY = 'church_erp_last_module';

// The home screen, wired to the router. "Last Visited" now survives a refresh
// because it is remembered in sessionStorage rather than component state.
export interface DashboardRouteProps {
  user: AuthUser | null;
}

const DashboardRoute = ({ user }: DashboardRouteProps) => {
  const navigate = useNavigate();
  const config = useChurchConfig();

  // A Sunday School teacher has one module; the tile grid would be a single
  // tile and a page of blank counts, so they go straight to their sections.
  const teacher = isTeacherLogin(user);
  useEffect(() => {
    if (teacher) navigate(pathForModuleId('Sunday School'), { replace: true });
  }, [teacher, navigate]);

  const hasAccess = (moduleId: string) => hasModuleAccess(user, moduleId);

  // The grid is the permission set, minus the modules the parish switched off
  // under Settings › Modules, with any tile titles it renamed there, category
  // by category in the order it numbered them — and the Settings tile itself
  // for an administrator.
  const hidden = new Set(parseHiddenModules(config));
  const titles = parseModuleTitles(config);
  const permittedModules = arrangeModules(MODULES, MODULE_CATEGORIES, parseCategoryOrder(config), parseModuleOrder(config))
    .filter((mod) => hasAccess(mod.id) && !hidden.has(mod.id))
    .map((mod) => (titles[mod.id] ? { ...mod, title: titles[mod.id] } : mod));
  const visibleModules = checkAdmin(user) ? [...permittedModules, SETTINGS_TILE] : permittedModules;

  const lastActiveModule = sessionStorage.getItem(LAST_VISITED_KEY);

  const openModule = (moduleId: string) => {
    sessionStorage.setItem(LAST_VISITED_KEY, moduleId);
    navigate(pathForModuleId(moduleId));
  };

  return (
    <DashboardHome
      modules={visibleModules}
      lastActiveModule={lastActiveModule}
      onSelectModule={openModule}
      hasAccess={hasAccess}
      userName={user?.name}
    />
  );
};

export default DashboardRoute;
